import { useState } from 'react';
import type { Category } from '@voice/shared';

interface ManualClassifierProps {
  categories: Category[];
  onSave: (data: { intent: string; category_id: string | null }) => void;
  loading: boolean;
}

export function ManualClassifier({ categories, onSave, loading }: ManualClassifierProps) {
  const [intent, setIntent] = useState<'note' | 'task' | 'reminder'>('note');
  const [categoryId, setCategoryId] = useState<string | null>(null);

  const approvedCategories = categories.filter((c) => c.approved);

  return (
    <div style={styles.card}>
      <p style={styles.title}>Manual classification required</p>
      <p style={styles.hint}>AI could not confidently classify this note. Please classify it manually.</p>

      {/* Intent */}
      <div style={styles.field}>
        <label style={styles.label}>Intent</label>
        <div style={styles.radioGroup}>
          {(['note', 'task', 'reminder'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              style={{
                ...styles.radioBtn,
                ...(intent === opt ? styles.radioBtnActive : {}),
              }}
              onClick={() => setIntent(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Category */}
      <div style={styles.field}>
        <label style={styles.label}>Category</label>
        <div style={styles.categoryGrid}>
          <button
            type="button"
            style={{
              ...styles.categoryBtn,
              ...(categoryId === null ? styles.categoryBtnActive : {}),
            }}
            onClick={() => setCategoryId(null)}
          >
            None
          </button>
          {approvedCategories.map((c) => (
            <button
              key={c.id}
              type="button"
              style={{
                ...styles.categoryBtn,
                ...(categoryId === c.id ? styles.categoryBtnActive : {}),
                borderColor: c.color ?? 'var(--color-border)',
              }}
              onClick={() => setCategoryId(c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <button
        style={styles.saveBtn}
        onClick={() => onSave({ intent, category_id: categoryId })}
        disabled={loading}
        type="button"
      >
        {loading ? 'Saving...' : 'Save classification'}
      </button>
    </div>
  );
}

const styles = {
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-3)',
    padding: 'var(--space-3)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
  },
  title: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
  },
  hint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    lineHeight: 1.5,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-1)',
  },
  label: {
    fontSize: '0.625rem',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  radioGroup: {
    display: 'flex',
    gap: 'var(--space-1)',
  },
  radioBtn: {
    padding: '4px 12px',
    fontSize: 'var(--font-size-xs)',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
  },
  radioBtnActive: {
    backgroundColor: 'var(--color-accent-light)',
    borderColor: 'var(--color-accent)',
    color: 'var(--color-accent)',
    fontWeight: 600,
  },
  categoryGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px',
  },
  categoryBtn: {
    padding: '4px 10px',
    fontSize: 'var(--font-size-xs)',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
  },
  categoryBtnActive: {
    backgroundColor: 'var(--color-accent-light)',
    borderColor: 'var(--color-accent)',
    color: 'var(--color-accent)',
    fontWeight: 600,
  },
  saveBtn: {
    padding: 'var(--space-2) var(--space-4)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'var(--color-accent)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    alignSelf: 'flex-end' as const,
  },
};
